import React from 'react';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import Link from 'next/link';
import Image from 'next/image';
import { Row, Divider, Col, List, Tooltip, Carousel } from 'antd';
import sanitizeHtml from 'sanitize-html';
import BasicLayout from 'layout/Basic';
import { convertProps } from 'lib/parseAPIResponse';
import { SubTitle } from 'components/PageTitle';
import Avatar from 'components/Avatar';
import SocialLinks from 'components/SocialLinks';
import Pagination from 'components/Pagination';
import Section from 'components/Section';
import { SkyscraperMobile } from 'components/Ads';
import Item from 'components/List/ArticleItem';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import CompanyImage from 'components/CompanyImage';
import BG from 'public/static/images/author_background.png';
import { merriweather } from 'styles/GlobalStyle';

interface Props {
    articles: Paginated<Article>;
    author: Author;
    ads: PageAds;
}

const CompanyLogoContainer = styled.div`
    position: absolute;
    height: auto;
    top: 50%;
    left: 3%;
    @media (max-width: 992px) {
        top: 55%;
        left: 50%;
        transform: translateX(-50%);
        > img {
            max-width: 140px;
        }
    }
    @media (max-width: 576px) {
        top: 15%;
    }
`;

const AuthorName = styled.p`
    font-size: 32px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 32px;
    letter-spacing: normal;
    text-align: left;
    @media (max-width: 992px) {
        text-align: center;
        margin-bottom: 0.5em;
    }
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const AuthorDetails = styled.p`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    @media (max-width: 992px) {
        text-align: center;
    }
    margin-bottom: 0;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const AboutAuthorText = styled.div`
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    @media (max-width: 992px) {
        text-align: center;
    }
    color: ${(props) => props.color || props.theme.colors.brownishGrey};
`;

const LogoWrapper = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.lightBlue};
`;

export const ArticleDetailsPage = (props: Props): React.JSX.Element => {
    const router = useRouter();

    const screens = useBreakpoint();

    const onChange = (page) => {
        router.push(`${router.query.slug}?page=${page}`);
    };

    const { author, articles, ads } = props;
    const avatarSize = screens.md ? 300 : 150;
    const { skyscraper } = ads;
    return (
        <BasicLayout ads={ads}>
            <Row>
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="0 auto" />
                </Col>
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[1]} margin="35px auto 35px auto" />
                </Col>
            </Row>
            <Row style={{ position: 'relative' }}>
                <Col span={24}>
                    <Image width={1200} height={303} src={BG} alt={author.name} />
                    <CompanyLogoContainer>
                        <Avatar
                            person={author}
                            photoDimensions={{ width: avatarSize, height: avatarSize }}
                            size={avatarSize}
                        />
                    </CompanyLogoContainer>
                </Col>
            </Row>
            <Section $marginTop={screens.md ? 35 : 80}>
                <Col md={{ offset: 7, span: 11 }} xs={24}>
                    <AuthorName>{author.name}</AuthorName>
                    <AuthorDetails>{author.position}</AuthorDetails>
                    <AuthorDetails>{author.companyName}</AuthorDetails>
                </Col>
                {author.socialLinks && (
                    <Col xs={24} md={6}>
                        <Row justify="end" wrap>
                            <Col>
                                <SocialLinks {...author.socialLinks} />
                            </Col>
                        </Row>
                    </Col>
                )}
            </Section>
            <Section $marginTop={screens.md ? 35 : 0}>
                <Col span={24}>
                    <Divider />
                </Col>
            </Section>
            {author.about && (
                <Section gutter={16}>
                    <Col xs={24} md={7}>
                        <SubTitle>O AUTORZE</SubTitle>
                    </Col>
                    <Col xs={24} md={11}>
                        <AboutAuthorText
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(author.about) }}
                        />
                    </Col>
                    {author.companies.length > 0 && (
                        <Col span={6}>
                            {author.companies.length < 2 ? (
                                <LogoWrapper>
                                    <Link href={`/katalog-firm/${author.companies[0].seoSlug}`}>
                                        <Tooltip title={author.companies[0].name}>
                                            <CompanyImage patron={author.companies[0]} />
                                        </Tooltip>
                                    </Link>
                                </LogoWrapper>
                            ) : (
                                <Carousel dotPosition="left" autoplay autoplaySpeed={7000}>
                                    {author.companies.map((company) => (
                                        <LogoWrapper key={company.seoSlug}>
                                            <Link href={`/katalog-firm/${company.seoSlug}`}>
                                                <Tooltip title={company.name}>
                                                    <CompanyImage patron={company} />
                                                </Tooltip>
                                            </Link>
                                        </LogoWrapper>
                                    ))}
                                </Carousel>
                            )}
                        </Col>
                    )}
                    <Col span={24}>
                        <Divider />
                    </Col>
                </Section>
            )}
            <Section>
                <Col span={18}>
                    <Row justify="center">
                        <Col span={24}>
                            <List
                                itemLayout="horizontal"
                                dataSource={articles.items}
                                renderItem={(item: Article) => <Item $article={item} />}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col span={24}>
                    <Pagination
                        currentPage={articles.currentPage}
                        results={articles.results}
                        onChange={onChange}
                        next={articles.next}
                        prev={articles.prev}
                    />
                </Col>
            </Section>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params, query }) => {
    const slug = params.slug;
    const page = query.page || 1;
    const { apiUrl } = getConfig().publicRuntimeConfig;

    const path = `/public/api/authors/${slug},${page}`;
    const url = apiUrl + path;
    return Promise.all([fetch(url), fetch(`${apiUrl}/public/api/naszadsik?path=autorzy`)]).then(
        async ([res1, res2]) => {
            const data = await res1.json();
            if (data.code === 404) {
                return {
                    notFound: true
                };
            }
            const ads = await res2.json();

            return { props: { ...convertProps(ads), ...convertProps(data) } };
        }
    );
};

export default ArticleDetailsPage;
